/**
 * Form.io file-upload wiring — makes patient-facing forms upload attachments to
 * ClearIntake without changing anything else about a form.
 *
 *  - transformFileComponents(schema, uploadUrl): deep-clone the schema and, for every
 *    `type:'file'` component, set `storage:'url'` + `url` (our upload endpoint). Non-file
 *    components are left byte-for-byte identical, so existing forms render unchanged.
 *  - setFormioToken(): push the current session JWT into Form.io's token store so the
 *    `url` storage provider sends it as `x-jwt-token` on the upload/download XHR (this is
 *    Form.io's documented auth path — `provider.getToken()`; a schema-embedded
 *    `component.options.headers` object is NOT reliably forwarded by the renderer).
 *
 * Only the patient portal / public form surfaces call setFormioToken, and only ever with
 * a short-lived CONTACT JWT (staff never upload). clearFormioToken() drops it on unmount.
 */
import { Formio } from 'formiojs';
import { API_BASE, getAuthToken } from './api.js';

/** The URL the Form.io `url` provider POSTs each file to. Query carries form + tenant so
 *  the endpoint can authorize (incl. anonymous public forms) and scope the pending row. */
export function fileUploadUrl(formId: string, locationId: string): string {
  const q = new URLSearchParams({ formId, locationId });
  return `${API_BASE}/api/forms/files?${q.toString()}`;
}

type JsonObject = Record<string, unknown>;

function cloneSchema<T>(schema: T): T {
  // Schemas are plain JSON — a round-trip is lossless and guarantees we never mutate
  // the caller's object.
  return JSON.parse(JSON.stringify(schema)) as T;
}

/**
 * Return a NEW schema with every file component pointed at `uploadUrl`. Walks
 * components[], columns[], and rows[][] so file fields nested in panels/columns/tables
 * are covered. Input is never mutated.
 */
export function transformFileComponents<T>(schema: T, uploadUrl: string): T {
  const clone = cloneSchema(schema);
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) { for (const n of node) visit(n); return; }
    if (!node || typeof node !== 'object') return;
    const c = node as JsonObject;
    if (c['type'] === 'file') {
      c['storage'] = 'url';
      c['url'] = uploadUrl;
    }
    if (Array.isArray(c['components'])) visit(c['components']);
    if (Array.isArray(c['columns'])) visit(c['columns']);
    if (Array.isArray(c['rows'])) { for (const r of c['rows'] as unknown[]) visit(r); }
  };
  visit(clone as unknown);
  return clone;
}

/** Put the current session JWT into Form.io's token store so file upload/download XHRs
 *  carry `x-jwt-token`. No-op when there's no token (anonymous public forms). */
export function setFormioToken(): void {
  const token = getAuthToken();
  if (token) Formio.setToken(token);
}

/** Drop the Form.io token (call on unmount of an upload-capable form). */
export function clearFormioToken(): void {
  try { Formio.setToken(''); } catch { /* ignore */ }
}

/** Convenience for a live render site: return the file-wired schema. Pair with a
 *  setFormioToken()/clearFormioToken() effect for the auth token. */
export function wireFormForUpload<T>(schema: T, formId: string, locationId: string): T {
  return transformFileComponents(schema, fileUploadUrl(formId, locationId));
}
